import { useState, useEffect } from "react";
import { Plus, CheckCircle, Pencil, Trash2, ChevronDown, ChevronUp, ArrowUp, ArrowDown, X } from "lucide-react";
import { Button } from "../../primitives/Button";
import { IconButton } from "../../primitives/IconButton";
import { Modal } from "../../primitives/Modal";
import { Input } from "../../primitives/Input";
import { Select } from "../../primitives/Select";
import {
  getDraftFull,
  getProcessTemplate,
  publishProcessTemplate,
  addStepToDraft,
  updateStep,
  deleteStep,
  reorderStep,
  addActivityToStep,
  updateActivity,
  deleteActivity,
  addTriggerToStep,
  updateTrigger,
  deleteTrigger,
  type ProcessTemplate,
  type ProcessStepTemplate,
  type ProcessActivityTemplate,
  type ProcessTriggerTemplate,
  type CompletionPolicy,
  type ActivityType,
  type TriggerEventType,
  type TriggerActionType,
} from "../../../api/processes";
import { ProcessStepCard } from "./ProcessStepCard";
import { StepEditorModal } from "./StepEditorModal";
import { ProcessTemplateActions } from "./ProcessTemplateActions";
import { ProcessTemplateValidationPanel } from "./ProcessTemplateValidationPanel";
import { ProcessConfigurationHeader } from "./ProcessConfigurationHeader";
import { ProcessStepsList } from "./ProcessStepsList";
import { cn } from "../../utils/cn";

type ProcessConfigurationTabProps = {
  templateId: string;
  onTemplateDeleted?: () => void;
};

type ProcessDetail = ProcessTemplate & {
  current_version?: {
    id: string;
    version_number: number;
    published_at: string | null;
    steps: (ProcessStepTemplate & {
      activities: ProcessActivityTemplate[];
    })[];
  };
};

export function ProcessConfigurationTab({ templateId, onTemplateDeleted }: ProcessConfigurationTabProps) {
  console.log("🔵 ProcessConfigurationTab mounted with templateId:", templateId);
  const [template, setTemplate] = useState<ProcessDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPublishing, setIsPublishing] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [stepModalOpen, setStepModalOpen] = useState(false);
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [editingStep, setEditingStep] = useState<ProcessStepTemplate | null>(null);
  const [editingActivity, setEditingActivity] = useState<{ stepId: string; activity: ProcessActivityTemplate | null } | null>(null);
  const [stepForm, setStepForm] = useState({
    order_index: "",
    title: "",
    description: "",
    sla_days: "",
    completion_policy: "all_required" as CompletionPolicy,
  });
  const [stepActivities, setStepActivities] = useState<Array<{
    id?: string;
    title: string;
    description: string;
    is_required: boolean;
    activity_type: ActivityType;
    order_index: number;
  }>>([]);
  const [stepTriggers, setStepTriggers] = useState<Array<{
    id?: string;
    title: string;
    description: string;
    event_type: TriggerEventType;
    action_type: TriggerActionType;
    action_payload: Record<string, any>;
  }>>([]);
  const [activityForm, setActivityForm] = useState({
    title: "",
    description: "",
    is_required: true,
    activity_type: "other" as ActivityType,
  });
  const [triggerForm, setTriggerForm] = useState({
    title: "",
    description: "",
    event_type: "step_started" as TriggerEventType,
    action_type: "create_alert" as TriggerActionType,
    action_payload: {} as Record<string, any>,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    console.log("🟡 useEffect triggered, templateId:", templateId);
    if (templateId) {
      loadTemplate();
    } else {
      console.error("❌ templateId is missing in useEffect!");
    }
  }, [templateId]);

  const loadTemplate = async () => {
    console.log("🟢 loadTemplate called with templateId:", templateId);
    if (!templateId) {
      console.error("❌ templateId is empty in loadTemplate!");
      alert("Erreur: ID du processus manquant");
      return;
    }
    setIsLoading(true);
    try {
      // Always try to load full draft first - it will create a draft if needed
      try {
        console.log("📞 Calling getDraftFull with templateId:", templateId);
        const fullData = await getDraftFull(templateId);
        console.log("✅ Loaded draft full:", {
          id: fullData.id,
          name: fullData.name,
          status: fullData.status,
          current_draft_version_id: fullData.current_draft_version_id,
          current_published_version_id: fullData.current_published_version_id,
          current_version: fullData.current_version ? {
            id: fullData.current_version.id,
            version_number: fullData.current_version.version_number,
            state: fullData.current_version.state,
            published_at: fullData.current_version.published_at,
            stepsCount: fullData.current_version.steps?.length || 0,
          } : null,
        });
        setTemplate(fullData as ProcessDetail);
      } catch (error: any) {
        // If getDraftFull fails, try basic template
        console.error("❌ getDraftFull FAILED:", error);
        console.error("Error details:", {
          message: error.message,
          status: error.status,
          response: error.response,
          stack: error.stack,
        });
        // Show error to user
        alert(`Erreur lors du chargement du draft: ${error.message || error}. Vérifiez la console pour plus de détails.`);
        try {
          const data = await getProcessTemplate(templateId, true);
          console.log("✅ Loaded basic template as fallback:", data);
          setTemplate(data as ProcessDetail);
        } catch (err: any) {
          console.error("❌ Failed to load process template:", err);
          alert(`Erreur lors du chargement: ${err.message || err}`);
        }
      }
    } catch (error: any) {
      console.error("Failed to load process template:", error);
      alert(`Erreur lors du chargement: ${error.message || error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePublish = async () => {
    if (!template) return;
    if (!confirm("Êtes-vous sûr de vouloir publier ce processus ? Cette action est irréversible.")) {
      return;
    }

    setIsPublishing(true);
    try {
      await publishProcessTemplate(template.id);
      await loadTemplate();
      alert("Processus publié avec succès !");
    } catch (error: any) {
      console.error("Failed to publish process template:", error);
      alert(`Erreur lors de la publication: ${error.message || error}`);
    } finally {
      setIsPublishing(false);
    }
  };

  const toggleStep = (stepId: string) => {
    const newExpanded = new Set(expandedSteps);
    if (newExpanded.has(stepId)) {
      newExpanded.delete(stepId);
    } else {
      newExpanded.add(stepId);
    }
    setExpandedSteps(newExpanded);
  };

  const handleAddStep = () => {
    setEditingStep(null);
    const steps = template?.current_version?.steps || [];
    const maxOrder = steps.length > 0 ? Math.max(...steps.map((s) => s.order_index)) : 0;
    setStepForm({
      order_index: (maxOrder + 1).toString(),
      title: "",
      description: "",
      sla_days: "",
      completion_policy: "all_required",
    });
    setStepActivities([]);
    setStepTriggers([]);
    setErrors({});
    setStepModalOpen(true);
  };

  const handleEditStep = async (step: ProcessStepTemplate) => {
    setEditingStep(step);
    // Load step details with activities and triggers
    try {
      const stepDetails = await getProcessTemplate(templateId, true);
      const stepWithDetails = stepDetails.current_version?.steps?.find((s) => s.id === step.id);
      if (stepWithDetails) {
        setStepModalOpen(true);
      } else {
        setStepModalOpen(true);
      }
    } catch (error) {
      console.error("Failed to load step details:", error);
      setStepModalOpen(true);
    }
    setErrors({});
  };

  const handleDeleteStep = async (stepId: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cette étape ?")) {
      return;
    }
    try {
      await deleteStep(stepId);
      await loadTemplate();
    } catch (error: any) {
      alert(`Erreur: ${error.message || error}`);
    }
  };

  const handleSaveStep = async (data: {
    order_index: number;
    title: string;
    description: string | null;
    sla_days: number | null;
    completion_policy: CompletionPolicy;
    activities: Array<{
      id?: string;
      title: string;
      description: string;
      is_required: boolean;
      activity_type: ActivityType;
      order_index: number;
    }>;
    triggers: Array<{
      id?: string;
      title: string;
      description: string;
      event_type: TriggerEventType;
      action_type: TriggerActionType;
      action_payload: Record<string, any>;
    }>;
  }) => {
    try {
      let stepId: string;
      if (editingStep) {
        await updateStep(editingStep.id, {
          order_index: data.order_index,
          title: data.title,
          description: data.description,
          sla_days: data.sla_days,
          completion_policy: data.completion_policy,
          entry_conditions: data.entry_conditions,
          exit_conditions: data.exit_conditions,
        });
        stepId = editingStep.id;
      } else {
        const newStep = await addStepToDraft(templateId, {
          order_index: data.order_index,
          title: data.title,
          description: data.description,
          sla_days: data.sla_days,
          completion_policy: data.completion_policy,
          entry_conditions: data.entry_conditions,
          exit_conditions: data.exit_conditions,
        });
        stepId = newStep.id;
      }

      // Get existing activities to delete removed ones (only when editing)
      if (editingStep) {
        const stepDetails = await getProcessTemplate(templateId, true);
        const stepWithDetails = stepDetails.current_version?.steps?.find((s) => s.id === stepId);
        const existingActivityIds = new Set((stepWithDetails?.activities || []).map((a) => a.id));
        const newActivityIds = new Set(data.activities.filter((a) => a.id).map((a) => a.id!));
        const activitiesToDelete = Array.from(existingActivityIds).filter((id) => !newActivityIds.has(id));
        for (const activityId of activitiesToDelete) {
          await deleteActivity(activityId);
        }
      }

      // Save activities
      for (const activity of data.activities) {
        const payload = {
          title: activity.title,
          description: activity.description || null,
          is_required: activity.is_required,
          activity_type: activity.activity_type,
          ...(typeof (activity as { checklist_config?: unknown }).checklist_config !== "undefined"
            ? { checklist_config: (activity as { checklist_config?: unknown }).checklist_config ?? null }
            : {}),
          ...(typeof (activity as { cost_activity_config?: unknown }).cost_activity_config !== "undefined"
            ? { cost_activity_config: (activity as { cost_activity_config?: unknown }).cost_activity_config ?? null }
            : {}),
          ...(typeof (activity as { assignee_activity_config?: unknown }).assignee_activity_config !== "undefined"
            ? { assignee_activity_config: (activity as { assignee_activity_config?: unknown }).assignee_activity_config ?? null }
            : {}),
          ...(typeof (activity as { activity_ui_config?: unknown }).activity_ui_config !== "undefined"
            ? { activity_ui_config: (activity as { activity_ui_config?: unknown }).activity_ui_config ?? null }
            : {}),
        };
        if (activity.id) {
          await updateActivity(activity.id, payload);
        } else {
          await addActivityToStep(stepId, payload);
        }
      }

      // Save triggers
      for (const trigger of data.triggers) {
        if (trigger.id) {
          await updateTrigger(trigger.id, {
            step_template_id: stepId,
            event_type: trigger.event_type,
            action_type: trigger.action_type,
            action_payload: trigger.action_payload,
          });
        } else {
          await addTriggerToStep(stepId, {
            step_template_id: stepId,
            event_type: trigger.event_type,
            action_type: trigger.action_type,
            action_payload: trigger.action_payload,
          });
        }
      }

      await loadTemplate();
      setStepModalOpen(false);
      setEditingStep(null);
    } catch (error: any) {
      console.error("Failed to save step:", error);
      throw error;
    }
  };

  const handleAddActivity = (stepId: string) => {
    setEditingActivity({ stepId, activity: null });
    setActivityForm({
      title: "",
      description: "",
      is_required: true,
      activity_type: "other",
    });
    setErrors({});
    setActivityModalOpen(true);
  };

  const handleEditActivity = (stepId: string, activity: ProcessActivityTemplate) => {
    setEditingActivity({ stepId, activity });
    setActivityForm({
      title: activity.title,
      description: activity.description || "",
      is_required: activity.is_required,
      activity_type: activity.activity_type,
    });
    setErrors({});
    setActivityModalOpen(true);
  };

  const handleDeleteActivity = async (activityId: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cette activité ?")) {
      return;
    }
    try {
      await deleteActivity(activityId);
      await loadTemplate();
    } catch (error: any) {
      alert(`Erreur: ${error.message || error}`);
    }
  };

  const handleSubmitActivity = async () => {
    setErrors({});
    if (!activityForm.title.trim()) {
      setErrors({ title: "Le titre est requis" });
      return;
    }

    if (!editingActivity) return;

    try {
      if (editingActivity.activity) {
        await updateActivity(editingActivity.activity.id, {
          title: activityForm.title,
          description: activityForm.description || null,
          is_required: activityForm.is_required,
          activity_type: activityForm.activity_type,
        });
      } else {
        await addActivityToStep(editingActivity.stepId, {
          title: activityForm.title,
          description: activityForm.description || null,
          is_required: activityForm.is_required,
          activity_type: activityForm.activity_type,
        });
      }
      setActivityModalOpen(false);
      await loadTemplate();
    } catch (error: any) {
      alert(`Erreur: ${error.message || error}`);
    }
  };

  if (isLoading) {
    return <div className="p-8 text-center text-muted-foreground">Chargement...</div>;
  }

  if (!template) {
    return <div className="p-8 text-center text-muted-foreground">Processus introuvable</div>;
  }

  // Can edit if template is active (not archived)
  // getDraftFull will create a draft automatically, so we should always have a current_version after loading
  const isActive = template.status === "active";
  const hasVersion = template.current_version !== null && template.current_version !== undefined;
  // If no version yet, we can still edit (draft will be created on first action)
  // If version exists, check if it's draft (not published)
  const isDraft = !hasVersion || template.current_version?.published_at === null || template.current_version?.state === "draft";
  const canEdit = isActive && isDraft;
  const steps = template.current_version?.steps || [];
  
  // Debug logging - ALWAYS log to console with emojis for visibility
  console.log("🔍 ProcessConfigurationTab - canEdit check:", {
    isActive,
    hasVersion,
    isDraft,
    canEdit,
    templateStatus: template.status,
    currentVersion: template.current_version,
    stepsCount: steps.length,
  });

  const handleReorderStep = async (stepId: string, direction: "up" | "down") => {
    try {
      const step = steps.find((s) => s.id === stepId);
      if (!step) return;
      
      const currentIndex = step.order_index;
      const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
      
      if (newIndex < 1) return; // Can't go below 1
      
      await reorderStep(stepId, newIndex);
      await loadTemplate();
    } catch (error: any) {
      alert(`Erreur lors du réordonnancement: ${error.message || error}`);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <ProcessConfigurationHeader template={template} onUpdate={loadTemplate} onTemplateDeleted={onTemplateDeleted} />
      {canEdit && (
        <div className="border-b border-border bg-surface p-4">
          <ProcessTemplateValidationPanel templateId={templateId} />
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4">
        {canEdit ? (
          <ProcessStepsList
            steps={steps}
            canEdit={canEdit}
            onAddStep={handleAddStep}
            onEditStep={handleEditStep}
            onDeleteStep={handleDeleteStep}
            onReorderStep={handleReorderStep}
            expandedSteps={expandedSteps}
            onToggleStep={toggleStep}
          />
        ) : (
          <div className="p-8 text-center text-muted-foreground">
            {template.status === "archived" ? (
              <div>
                <p className="mb-2">Ce processus est archivé et ne peut plus être modifié.</p>
                <p className="text-sm">Utilisez "Cloner" pour créer une nouvelle version.</p>
              </div>
            ) : !hasVersion ? (
              <div>
                <p className="mb-2">Aucune version trouvée. Création d'une version draft...</p>
                <button
                  onClick={async () => {
                    try {
                      setIsLoading(true);
                      // Force reload with getDraftFull to create draft
                      const fullData = await getDraftFull(templateId);
                      setTemplate(fullData as ProcessDetail);
                    } catch (error: any) {
                      alert(`Erreur lors de la création du draft: ${error.message || error}`);
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                  className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
                >
                  Créer une version draft
                </button>
              </div>
            ) : (
              <div>
                <p className="mb-2">Ce processus est publié et ne peut plus être modifié directement.</p>
                <p className="text-sm">Une version draft sera créée automatiquement lors de la première modification.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Step Editor Modal */}
      <StepEditorModal
        open={stepModalOpen}
        onOpenChange={setStepModalOpen}
        step={editingStep}
        nextOrderIndex={
          editingStep == null
            ? (() => {
                const steps = template?.current_version?.steps || [];
                return steps.length > 0 ? Math.max(...steps.map((s) => s.order_index)) + 1 : 1;
              })()
            : undefined
        }
        activities={editingStep ? (template?.current_version?.steps?.find((s) => s.id === editingStep.id)?.activities || []) : []}
        triggers={[]} // TODO: Load triggers when available in API
        onSave={handleSaveStep}
      />

      {/* Activity Modal */}
      <Modal
        open={activityModalOpen}
        onOpenChange={setActivityModalOpen}
        title={editingActivity?.activity ? "Modifier l'activité" : "Nouvelle activité"}
        showDefaultButtons={false}
        showCloseButton={true}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Titre *</label>
            <Input
              value={activityForm.title}
              onChange={(e) => setActivityForm({ ...activityForm, title: e.target.value })}
              placeholder="Titre de l'activité"
              error={errors.title}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <Input
              value={activityForm.description}
              onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })}
              placeholder="Description de l'activité"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-text">Type d'activité</label>
            <Select
              label=""
              hideLabel={true}
              value={
                activityForm.activity_type === "call"
                  ? "Appel"
                  : activityForm.activity_type === "email"
                    ? "Courriel"
                    : activityForm.activity_type === "upload"
                      ? "Téléversement"
                      : activityForm.activity_type === "analysis"
                        ? "Analyse"
                        : "Autre"
              }
              onChange={(value) => {
                const typeMap: Record<string, ActivityType> = {
                  Appel: "call",
                  Courriel: "email",
                  Téléversement: "upload",
                  Analyse: "analysis",
                  Autre: "other",
                };
                setActivityForm({ ...activityForm, activity_type: typeMap[value] || "other" });
              }}
              options={["Appel", "Courriel", "Téléversement", "Analyse", "Autre"]}
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_required"
              checked={activityForm.is_required}
              onChange={(e) => setActivityForm({ ...activityForm, is_required: e.target.checked })}
              className="rounded"
            />
            <label htmlFor="is_required" className="text-sm font-medium">
              Activité requise
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button
              label="Annuler"
              intent="default"
              size="sm"
              onClick={() => setActivityModalOpen(false)}
            />
            <Button
              label={editingActivity?.activity ? "Enregistrer" : "Créer"}
              intent="primary"
              size="sm"
              onClick={handleSubmitActivity}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
